import { useState, useEffect } from 'react';
import { APP_REGISTRY } from '../config/appRegistry';

const useWindowManager = () => {
    const [openWindows, setOpenWindows] = useState(() => {
        try {
            const saved = localStorage.getItem('xp_open_windows');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.error('Failed to load windows from storage', e);
            return [];
        }
    });

    const [openNativeApps, setOpenNativeApps] = useState(() => {
        try {
            const saved = localStorage.getItem('xp_open_native_apps');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.error('Failed to load native apps from storage', e);
            return [];
        }
    });

    // Persist state changes
    useEffect(() => {
        localStorage.setItem('xp_open_windows', JSON.stringify(openWindows));
    }, [openWindows]);

    useEffect(() => {
        localStorage.setItem('xp_open_native_apps', JSON.stringify(openNativeApps));
    }, [openNativeApps]);

    const handleOpenWindow = (windowType) => {
        const windowId = `${windowType} -${Date.now()} `;
        const appConfig = APP_REGISTRY[windowType];

        const newWindow = {
            id: windowId,
            type: windowType,
            isMaximized: false,
            x: (appConfig?.initialPosition?.x || 100) + openWindows.length * 30, // Offset for cascading
            y: (appConfig?.initialPosition?.y || 100) + openWindows.length * 30,
            width: appConfig?.defaultSize?.width || 600,
            height: appConfig?.defaultSize?.height || 500,
        };
        setOpenWindows([...openWindows, newWindow]);
    };

    const handleOpenNativeApp = (appType) => {
        // Check if app is already open
        if (openNativeApps.find(app => app.type === appType)) {
            return; // Don't open duplicate native apps
        }

        const appId = `${appType} -${Date.now()} `;
        const newApp = {
            id: appId,
            type: appType
        };
        setOpenNativeApps([...openNativeApps, newApp]);
    };

    const handleOpenApp = (appType) => {
        const appConfig = APP_REGISTRY[appType];
        if (!appConfig) {
            console.warn(`App type ${appType} not found in registry`);
            return;
        }

        if (appConfig.isNative) {
            handleOpenNativeApp(appType);
        } else {
            handleOpenWindow(appType);
        }
    };

    const handleCloseNativeApp = (appId) => {
        setOpenNativeApps(openNativeApps.filter(app => app.id !== appId));
    };

    const handleCloseWindow = (windowId) => {
        setOpenWindows(openWindows.filter(window => window.id !== windowId));
    };

    const handleMinimizeWindow = (windowId) => {
        setOpenWindows(openWindows.map(window =>
            window.id === windowId
                ? { ...window, isMinimized: true }
                : window
        ));
    };

    const handleMaximizeWindow = (windowId) => {
        setOpenWindows(openWindows.map(window =>
            window.id === windowId
                ? { ...window, isMaximized: !window.isMaximized }
                : window
        ));
    };

    const handleRestoreWindow = (windowId) => {
        setOpenWindows(openWindows.map(window =>
            window.id === windowId
                ? { ...window, isMinimized: false }
                : window
        ));
    };

    const handleToggleMinimizeWindow = (windowId) => {
        setOpenWindows(openWindows.map(window =>
            window.id === windowId
                ? { ...window, isMinimized: !window.isMinimized }
                : window
        ));
    };

    return {
        openWindows,
        openNativeApps,
        handleOpenApp,
        handleCloseWindow,
        handleCloseNativeApp,
        handleMinimizeWindow,
        handleMaximizeWindow,
        handleRestoreWindow,
        handleToggleMinimizeWindow,
    };
};

export default useWindowManager;
